import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { useRef, useState } from "react";
import {
  useConfirmUploadAsset,
  useDownloadAssets,
  useUploadAssetFile,
} from "../../../hooks";
import { UploadPreviewDialog } from "./ReviewConfirm";

export const UpdateReadingsModal = ({
  open,
  onClose,
  energyType = "Energy",
}: {
  open: boolean;
  onClose: () => void;
  energyType: string;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const typeMapping: Record<string, string> = {
    "Electric Utility": "electric_utility",
    "Generator Energy": "generator",
    "Renewable Energy": "renewable",
  };
  const [uploadResponse, setUploadResponse] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const uploadAssetFile = useUploadAssetFile();
  const uploadAssets = useConfirmUploadAsset();

  // fallback in case not found
  const mappedType = typeMapping[energyType] ?? "";
  const downloadAssets = useDownloadAssets(mappedType);
  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileUpload = async () => {
    const apiFormData = new FormData();

    if (selectedFile) {
      apiFormData.append("file", selectedFile);
    }
    const response = await uploadAssetFile.mutateAsync(apiFormData);
    setUploadResponse(response);
    setShowPreview(true);
    fileInputRef.current!.value = "";
    setSelectedFile(null);
  };

  const handleDownload = () => {
    downloadAssets.mutateAsync();
  };

  const handleClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const handleUploadAssets = async (payload: any) => {
    await uploadAssets.mutateAsync(payload);
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Update {energyType} Readings</DialogTitle>
        </DialogHeader>

        <p className="text-gray-600 mb-4">
          Download the CSV template, fill in the reading data, and upload.
        </p>

        {/* Step 1 */}
        <div className="space-y-2">
          <p className="font-medium">Step 1: Download CSV template</p>
          <Button
            variant="outline"
            className="w-full flex items-center gap-2"
            onClick={handleDownload}
            isLoading={downloadAssets.isPending}
          >
            📄 Download CSV Template
          </Button>
          <p className="text-xs text-gray-500">
            Note: Previous readings are auto-populated from the last entry if
            available.
          </p>
        </div>

        <div className="mt-4">
          <p className="font-medium mb-2">Step 2: Upload filled CSV</p>

          <div className="border-2 border-dashed rounded-md p-6 text-center space-y-3">
            <Button onClick={openFileDialog}>Upload CSV</Button>

            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".csv"
              onChange={handleFileSelect}
            />

            {/* Selected file name */}
            {selectedFile && (
              <p className="text-green-600 text-sm">
                Selected:
                <span className="font-medium">{selectedFile?.name}</span>
              </p>
            )}

            {/* Confirm button only if file is selected */}
            {selectedFile && (
              <Button
                variant="default"
                onClick={handleFileUpload}
                isLoading={uploadAssetFile.isPending}
              >
                Confirm & Preview Data
              </Button>
            )}
          </div>
        </div>

        {/* Close Button */}
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose} className="ml-auto">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
      <UploadPreviewDialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        data={uploadResponse}
        type={energyType}
        isUpdating={uploadAssets.isPending}
        onConfirm={(payload) => {
          handleUploadAssets(payload);
        }}
      />
    </Dialog>
  );
};
